import cx from 'classnames';
import { FC, useMemo } from 'react';

import { usePropertiesLookupList } from '@apis/propertiesApi';
import { ExistingProperty } from '@apis/propertiesApi.types';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Button } from '@shared/components/button-new';
import { Flex } from '@shared/components/flex';
import { Loading } from '@shared/components/loading';
import { CheckIcon } from '@shared/icons/check';
import { ButtonSize } from '@shared/types/common/button';

import { styles } from './PropertiesLookup.styles';

export interface PropertiesLookupProps extends WithStyles<typeof styles> {
  address: Address;
  requestEnabled?: boolean;
  selectedPropertyId: number | undefined;
  title: string;
  onSelectPropertyId: (id?: number) => void;
  onSkipStep: (id?: number) => void;
}

const PropertiesLookupComponent: FC<PropertiesLookupProps> = ({
  classes,
  address,
  requestEnabled = true,
  selectedPropertyId,
  title,
  onSelectPropertyId,
  onSkipStep,
}) => {
  const propertiesListLookup = usePropertiesLookupList(
    {
      query: {
        address_name: address.name,
        address_line1: address.line1,
        address_line2: address.line2,
        address_town: address.town,
        address_county: address.county,
        address_postcode: address.postcode,
        country: address.country,
      },
    },

    {
      enabled: requestEnabled,
      onSuccess: (response) => {
        if (response.data.length === 1 && response.data[0]?.exact_match) {
          onSelectPropertyId(response.data[0].id);
          onSkipStep(response.data[0].id); // Go to next step if only one property with exact_match: true

          return;
        }

        if (!response.data.length) {
          onSelectPropertyId(undefined);
          onSkipStep(); // Go to next step is properties not exist

          return;
        }

        onSelectPropertyId(undefined); // Need too reset property id if it's updating property in this case user need to select property again
      },
      onError: () => {
        onSelectPropertyId(undefined);
        onSkipStep(); // Go to next step is properties not exist
      },
    }
  );

  const propertiesListData = useMemo(() => propertiesListLookup.data?.data || [], [propertiesListLookup.data]);

  const normalisedAddress = useMemo(() => {
    return {
      building_name: address.name,
      county: address.county,
      line1: address.line1,
      line2: address.line2,
      postcode: address.postcode,
      town: address.town,
    };
  }, [address]);

  const renderPropertyInfo = (address: ExistingProperty['address']) => {
    const configInfo = [
      { key: t('address_line_1_1'), value: address.line1 },
      { key: t('address_line_2_1'), value: address.line2 },
      { key: t('town'), value: address.town },
      { key: t('county'), value: address.county },
      { key: t('postcode'), value: address.postcode },
    ];

    return configInfo.map((building) => (
      <Flex alignItems="center" wrap="nowrap" key={building.key}>
        <span className={classes.infoLabel}>{building.key}:</span>
        <span className={classes.infoValue}>{building.value || '-'}</span>
      </Flex>
    ));
  };

  if (propertiesListLookup.isFetching) {
    return (
      <div className={classes.root}>
        <Loading absolute />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Flex autoWidth={false} classes={{ root: classes.tile }} direction="column">
        <span className={classes.tileHeading}>{title}</span>
        <span className={classes.tileDescription}>
          {t('this_will_appear_on_your_marketing_listing_plus_any_other_publish_options')}
        </span>
        <Flex justifyContent="space-between" wrap="nowrap" classes={{ root: classes.building }}>
          <Flex direction="column">{renderPropertyInfo(normalisedAddress)}</Flex>
        </Flex>
      </Flex>

      <Flex autoWidth={false} classes={{ root: classes.tile }} direction="column">
        <span className={classes.tileHeading}>
          {t('you_have_activity_at_nearby_buildings_are_any_of_these_the_same_building_question')}
        </span>
        <span className={classes.tileDescription}>
          {t('accurately_identifying_the_same_building_helps_to_build_a_view_of_your_market')}
        </span>

        {/* Existing buildings */}
        {propertiesListData.map((property) => (
          <Flex
            key={property.id}
            classes={{
              root: cx(classes.building, classes.buildingSelectable, {
                [classes.buildingSelected]: property.id === selectedPropertyId,
              }),
            }}
            justifyContent="space-between"
            wrap="nowrap"
            onClick={() => onSelectPropertyId(property.id)}
          >
            <Flex direction="column">{renderPropertyInfo(property.address)}</Flex>
            <Button
              classes={{ root: classes.buildingButton }}
              size={ButtonSize.small}
              text={
                <>
                  {property.id === selectedPropertyId && <CheckIcon classes={{ root: classes.buildingCheckIcon }} />}
                  {t('yes_this_is_the_same_building')}
                </>
              }
            />
          </Flex>
        ))}

        {/* New building */}
        <Flex
          classes={{
            root: cx(classes.building, classes.buildingSelectable, {
              [classes.buildingSelected]: !selectedPropertyId,
            }),
          }}
          justifyContent="flex-end"
          onClick={() => onSelectPropertyId(0)}
        >
          <Button
            classes={{ root: classes.buildingButton }}
            size={ButtonSize.small}
            text={
              <>
                {selectedPropertyId === 0 && <CheckIcon classes={{ root: classes.buildingCheckIcon }} />}
                {t('no_the_disposal_is_at_a_new_building')}
              </>
            }
          />
        </Flex>
      </Flex>
    </div>
  );
};

export const PropertiesLookup = withStyles(styles)(PropertiesLookupComponent);
